const PNG_MIME_TYPE = 'image/png';

export type BackgroundRemovalDevice = 'cpu' | 'gpu';

export interface BackgroundRemovalOptions {
  /** 選擇執行裝置，預設使用 CPU 以提高相容性 */
  readonly device?: BackgroundRemovalDevice;
}

/** 可接受的輸入圖片 Blob 型別 */
export type ImageLikeBlob = Blob | File;

/**
 * 判斷是否為去背庫原生支援的 PNG 或 JPEG。
 */
export function isPngOrJpeg(blob: ImageLikeBlob): boolean {
  return blob.type === 'image/png' || blob.type === 'image/jpeg';
}

/**
 * 將任意圖片檔轉為 PNG Blob。
 *
 * 去背庫對部分格式（例如 AVIF/WebP）支援不穩定，統一先轉成 PNG 再送入模型，
 * 也方便後續在 Canvas 上進一步處理。
 */
export function toPngBlob(source: ImageLikeBlob): Promise<Blob> {
  if (isPngOrJpeg(source)) {
    return Promise.resolve(source);
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(source);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);

      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;

      const context = canvas.getContext('2d');
      if (!context) {
        reject(new Error('無法建立畫布'));
        return;
      }

      context.drawImage(img, 0, 0);
      canvas.toBlob(
        blob => {
          if (!blob) {
            reject(new Error('轉檔失敗'));
            return;
          }
          resolve(blob);
        },
        PNG_MIME_TYPE,
        1
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('圖片載入失敗'));
    };

    img.src = url;
  });
}

/**
 * 透過 @imgly/background-removal 進行去背。
 *
 * 只負責「輸入 Blob → 輸出去背後 Blob」，錯誤處理與 UI 由呼叫端負責。
 */
export async function removeBackgroundFromBlob(
  blob: ImageLikeBlob,
  options?: BackgroundRemovalOptions
): Promise<Blob> {
  const safeInput = await toPngBlob(blob);
  const { removeBackground } = await import('@imgly/background-removal');

  const device: BackgroundRemovalDevice = options?.device ?? 'cpu';
  const result = await removeBackground(safeInput, { device });

  return result;
}
